import { debounce } from 'lodash';
import {LookupItemModel} from "../../models";
import {isArabicCurrentLanguage} from "../localization/localizationManager";

interface SearchConfig<T> {
    // The search term
    searchValue: string;
    // Minimum characters to trigger API search
    minChars?: number;
    // The current list to check before making API call
    localList: T[];
    // Function to filter local items (returns true if item matches)
    localFilterFn?: (item: T, searchValue: string, isArabic: boolean) => boolean;
    // Function to call if no local matches found (API call)
    apiSearchFn: (searchValue: string) => Promise<void>;
    // Debounce time in ms
    debounceTime?: number;
}


// Default filter function for LookupItemModel items
const defaultLookupFilter = (item: LookupItemModel, searchValue: string, isArabic: boolean): boolean => {
    const itemName = isArabic ? item.nameAr : item.name;
    return Boolean(itemName && itemName.toLowerCase().includes(searchValue.toLowerCase()));
};


export const createDebouncedSearch = <T>(
    config: SearchConfig<T>
) => {
    const {
        searchValue,
        minChars = 3,
        localList,
        localFilterFn,
        apiSearchFn,
        debounceTime = 300
    } = config;

    const isArabic = isArabicCurrentLanguage();

    // If search term is too short, don't process
    if (!searchValue || searchValue.length < minChars) {
        return;
    }

    // For LookupItemModel, use default filter if none provided
    const filterFn = localFilterFn ||
        ((item: any, sv: string, ar: boolean) =>
            defaultLookupFilter(item as LookupItemModel, sv, ar));

    // Check local results first
    const hasLocalResults = localList.some(item =>
        filterFn(item, searchValue, isArabic)
    );

    // Only make API call if no local results
    if (!hasLocalResults) {
        // Create and execute debounced function
        const debouncedFn = debounce(async () => {
            try {
                await apiSearchFn(searchValue);
            } catch (error) {
                console.error('Error in API search:', error);
            }
        }, debounceTime);

        debouncedFn();
    }
};

// Usage example for the Select component's onInputChange handler
export const handleSelectInputChange = (
    inputValue: string,
    action: { action: string },
    config: Omit<SearchConfig<LookupItemModel>, 'searchValue'>
) => {
    if (action.action === 'input-change' && inputValue && inputValue.length >= (config.minChars || 3)) {
        createDebouncedSearch({
            ...config,
            searchValue: inputValue
        });
    }
    return inputValue;
};